import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";

import { fetchDataGetBlobFile } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";

interface Args {
  /** Identifiant de la demande de relevé (repris du `downloadUrl` de `request`). */
  statementId: string;
}

/**
 * Télécharge le PDF d'un relevé (`GET /Statements/requests/{statementId}/download`,
 * guide v3.1). Le document n'est pas conservé côté serveur : il est recomposé
 * à la demande. Le nom de fichier vient du header `Content-Disposition`,
 * avec repli `releve-<statementId>.pdf`. GET de consultation — hors scope
 * des previews (règle preview : mutations uniquement).
 */
export const useDownloadStatement = () =>
  useMutation({
    mutationKey: ["wallet", "statements", "download"],
    mutationFn: async ({ statementId }: Args) => {
      const { blob, filename } = await fetchDataGetBlobFile({
        url: walletEndpoints.statements.download(statementId),
        acceptHeader: "application/pdf",
      });
      if (blob.type.includes("json")) {
        const text = await blob.text();
        throw new Error(text || "Statement download failed");
      }
      saveAs(blob, filename ?? `releve-${statementId}.pdf`);
      return filename;
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDownloadStatement failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
